import { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Container, FormControl, Grid, InputLabel, MenuItem, Paper, Select, TableCell, TablePagination, TableRow, TextField } from '@mui/material'
import { OrganizationContext } from '../contexts/OrganizationContext';
import reportService from '../services/ReportService';
import { iProductivityRecord, iProductivityRecordSearch } from '../utilities/APIInterfaces';
import { formatDateTime } from '../utilities/HelperFunctions';
import ReportTable from '../components/reports/ReportTable';
import { tableRowsPerPageOptions } from '../utilities/Misc';
import { cardSX, inceptiaGreenAlphaColors } from '../utilities/CSS';
import { DropdownStyles } from '../components/notifications/ManageCrudPage';
import { Status, StatusLabel } from '../utilities/FormEnums';

const tdSX = {
  py: 1.125
};

const ProductivityReport = () => {
  const { organization } = useContext(OrganizationContext);
  const [isReportLoaded, setIsReportLoaded] = useState(false);
  const [productivityReport, setProductivityReport] = useState<iProductivityRecord[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const initialSearchModel : iProductivityRecordSearch = {
    opeId: '',
    form: '',
    submittedBy: '',
    assignedTo: '',
    statusAfterInReview: 0,
    sentToInReviewStartDate: '',
    sentToInReviewEndDate: '',
    reviewCompletedStartDate: '',
    reviewCompletedEndDate: ''
  };
  const [searchModel, setSearchModel] = useState<iProductivityRecordSearch>(initialSearchModel);

  useEffect(() => {
    if (organization?.opeid){
      setSearchModel({...searchModel, opeId: organization.opeid});
      GetReportAsync();
    }
}, [organization]);

  const GetReportAsync = async () => {
    setIsReportLoaded(false);
    await reportService.GetProductivityReport(organization!.opeid, (v) => v).then(result => {
      setProductivityReport(result);
      setIsReportLoaded(true);
    }).catch(error => {
        console.log(`Productivity Report ${error}`);
    });
  };

  const SearchReportAsync = useCallback(async () => {
    setIsReportLoaded(false);
    setPage(0);
    await reportService.SearchProductivityReport(searchModel, (v) => v).then(result => {
      setProductivityReport(result);
      setIsReportLoaded(true);
    }).catch(error => {
      console.log(`Productivity Report ${error}`);
    });
  }, [searchModel]);

  const handlePageChange = (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setRowsPerPage(parseInt(e.target.value, 10));
      setPage(0);
  };
    
  const renderForm = () => {
    const mainSX = {
        backgroundColor: "InceptiaGreen.main",
        "&:hover": {
            backgroundColor: "InceptiaGreen.dark"
        }
    };

    const secondarySX = {
        color: "InceptiaGreen.main",
        borderColor: "InceptiaGreen.main",
        "&:hover": {
            borderColor: "InceptiaGreen.dark",
            backgroundColor: inceptiaGreenAlphaColors.hover
        }
    };

    return (<>
      <Paper sx={{ ...cardSX.paper, mb: 4.5 }}>
        <Container sx={{ py: 4.5 }}>
          <Grid container gap={2}>
            <Grid container rowGap={4} columnSpacing={3.5} sx={{ mb: 1 }}>
              <Grid item xs={12} md={3}>
                <TextField
                  id="form"
                  label="Form"
                  fullWidth
                  value={searchModel.form}
                  onChange={
                    e => setSearchModel(model => ({ ...model, form: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  id="submittedBy"
                  label="Submitted By"
                  fullWidth
                  value={searchModel.submittedBy}
                  onChange={
                    e => setSearchModel(model => ({ ...model, submittedBy: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  id="assignedTo"
                  label="AssignedTo"
                  fullWidth
                  value={searchModel.assignedTo}
                  onChange={
                    e => setSearchModel(model => ({ ...model, assignedTo: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth sx={DropdownStyles}>
                    <InputLabel id="status-label">Status After In Review</InputLabel>
                    <Select
                      labelId="status-label"
                      id="statusAfterInReviewId"
                      label="Status After In Review"
                      value={searchModel.statusAfterInReview}
                      onChange={
                        e => setSearchModel(model => ({ ...model, statusAfterInReview: e.target.value as number }))
                      }
                    >
                      <MenuItem value={0}>All</MenuItem>
                      {Object.values(Status)
                        .filter(Number.isInteger)
                        .filter(status => status !== Status.REMOVED && status !== Status.IN_REVIEW)
                        .map(status => (
                          <MenuItem
                            key={status}
                            value={status}
                          >
                            {StatusLabel.get(Number(status))}
                          </MenuItem>
                        ))
                      }
                    </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  type="date"
                  id="sentToInReviewStartDate"
                  label="Sent To In Review Start Date"
                  InputLabelProps={{
                    shrink: true
                  }}
                  fullWidth
                  value={searchModel.sentToInReviewStartDate}
                  onChange={
                    e => setSearchModel(model => ({ ...model, sentToInReviewStartDate: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  type="date"
                  id="sentToInReviewEndDate"
                  label="Sent To In Review End Date"
                  InputLabelProps={{
                    shrink: true
                  }}
                  fullWidth
                  value={searchModel.sentToInReviewEndDate}
                  onChange={
                    e => setSearchModel(model => ({ ...model, sentToInReviewEndDate: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  type="date"
                  id="reviewCompletedStartDate"
                  label="Review Completed Start Date"
                  InputLabelProps={{
                    shrink: true
                  }}
                  fullWidth
                  value={searchModel.reviewCompletedStartDate}
                  onChange={
                    e => setSearchModel(model => ({ ...model, reviewCompletedStartDate: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  type="date"
                  id="reviewCompletedEndDate"
                  label="Review Completed End Date"
                  InputLabelProps={{
                    shrink: true
                  }}
                  fullWidth
                  value={searchModel.reviewCompletedEndDate}
                  onChange={
                    e => setSearchModel(model => ({ ...model, reviewCompletedEndDate: e.target.value }))
                  }
                />
              </Grid>
            </Grid>
            <Button
              sx={mainSX}
              variant="contained"
              color="primary"
              type="submit"
              disabled={!isReportLoaded}
              onClick={SearchReportAsync}
            >
              Search
            </Button>
            <Button variant="outlined" color="warning"
              onClick={() => setSearchModel({ ...initialSearchModel, opeId: organization?.opeid})}
              sx={secondarySX}
            >
              Clear
            </Button>
          </Grid>
        </Container>
      </Paper>
    </>);
  };

  return (<>
    <ReportTable 
      report_title='Productivity'
      accessibility_title='Productivity Report'
      form={renderForm()}
      empty_message='No records found...'
      is_loaded={isReportLoaded}
      table_data={productivityReport}
      rowsPerPage={rowsPerPage}
      page={page}
      hasExport={true}
      csvFileName='ProductivityReport.csv'
      tableHeaders={["Form", "Submitted By", "Assigned To", "Sent To In Review", "Status After In Review", "Review Completed", "Current Status"]}
      sortableColumns={["form", "submittedBy", "assignedTo", "sentToInReview", "statusAfterInReview", "reviewCompleted", "currentStatus"]}
      foot_notes={<>
        <TablePagination
          component='div'
          count={productivityReport?.length || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          rowsPerPageOptions={tableRowsPerPageOptions}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          sx={{
              '&, & .MuiTablePagination-input .MuiSvgIcon-root': { color: '#000' }
          }}
          slotProps={{
            actions: {
              previousButton: { 'aria-label': 'previous page' },
              nextButton: { 'aria-label': 'next page' }
            },
            // MUI generates WAVE "Missing form label" error, the options below fix it
            // https://stackoverflow.com/questions/66642553/tablepagination-mui-missing-form-label-accessibility-issue-in-wave-tool
            select: {
              id: 'rows-per-page-select',
              inputProps: {
                  'aria-label': 'rows per page',
                  'aria-labelledby': 'rows-per-page-select'
              }
            }
          }}
        />
      </>}
      render_row={(row: iProductivityRecord) => {
        return (
          <TableRow key={row.submissionFormId}>
            <TableCell sx={tdSX}>{row.form}</TableCell>
            <TableCell sx={tdSX}>{row.submittedBy}</TableCell>
            <TableCell sx={tdSX}>{row.assignedTo}</TableCell>
            <TableCell sx={tdSX}>{row.sentToInReview ? formatDateTime(row.sentToInReview) : ""}</TableCell>
            <TableCell sx={tdSX}>{row.statusAfterInReview}</TableCell>
            <TableCell sx={tdSX}>{row.reviewCompleted ? formatDateTime(row.reviewCompleted) : ""}</TableCell>
            <TableCell sx={tdSX}>{row.currentStatus}</TableCell>
          </TableRow>
        );
      }}
    />
  </>);
};

export default ProductivityReport;
